
import { useEffect, useRef } from "react";
import { Loader2, Home } from "lucide-react";
import type { ListingDetail } from "../../lib/jlApi";
import ListingCard from "./ListingCard";
import { SORT_OPTIONS, type SortKey } from "./listingUtils";

interface ListingsPanelProps {
  listings: ListingDetail[];
  totalCount: number | null;
  loading: boolean; // initial load (no data yet)
  refetching: boolean; // background refetch (dim existing)
  error: boolean;
  selectedId: number | null;
  comingSoon: boolean;
  sort: SortKey;
  onSortChange: (s: SortKey) => void;
  onSelect: (id: number) => void;
  onHover: (id: number | null) => void;
  isFavorite?: (listingId: string) => boolean;
  onToggleFavorite?: (listing: ListingDetail) => void;
  onInquire?: (listing: ListingDetail, mode: "ask" | "showing") => void;
  isComparing?: (id: number) => boolean;
  compareFull?: boolean;
  onToggleCompare?: (listing: ListingDetail) => void;
  className?: string;
}

function SkeletonCard() {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--sand)] bg-white">
      <div className="h-44 w-full animate-pulse bg-[var(--sand)]" />
      <div className="space-y-2 p-4">
        <div className="h-5 w-24 animate-pulse rounded bg-[var(--sand)]" />
        <div className="h-4 w-40 animate-pulse rounded bg-[var(--sand)]" />
        <div className="h-4 w-32 animate-pulse rounded bg-[var(--sand)]" />
      </div>
    </div>
  );
}

export default function ListingsPanel({
  listings,
  totalCount,
  loading,
  refetching,
  error,
  selectedId,
  comingSoon,
  sort,
  onSortChange,
  onSelect,
  onHover,
  isFavorite,
  onToggleFavorite,
  onInquire,
  isComparing,
  compareFull,
  onToggleCompare,
  className = "",
}: ListingsPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll the selected card into view when selection changes (pin -> card).
  useEffect(() => {
    if (selectedId == null || !scrollRef.current) return;
    const el = scrollRef.current.querySelector<HTMLElement>(
      `[data-listing-id="${selectedId}"]`
    );
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedId]);

  const headerCount =
    totalCount === null
      ? "Homes in view"
      : `${totalCount.toLocaleString()} ${totalCount === 1 ? "home" : "homes"} in view`;

  return (
    <div className={`flex min-h-0 flex-col bg-[var(--cream)] ${className}`}>
      {/* Header: count + sort */}
      <div className="flex items-center justify-between gap-2 border-b border-[var(--sand)] bg-white/80 px-4 py-2.5 backdrop-blur">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-[var(--navy)]">
          {(loading || refetching) && (
            <Loader2 size={13} className="animate-spin text-[var(--gold)]" />
          )}
          {headerCount}
        </span>
        <label className="flex items-center gap-1.5 text-xs text-[var(--slate)]">
          Sort
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortKey)}
            className="appearance-none rounded-lg border border-[var(--dune)] bg-white px-2 py-1.5 text-xs text-[var(--navy)] focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            aria-label="Sort listings"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Body */}
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto p-3 pb-24 lg:pb-3"
      >
        {loading && listings.length === 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error && listings.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <Home size={36} className="mb-3 text-[var(--dune)]" />
            <p className="font-medium text-[var(--navy)]">
              Couldn&apos;t load listings
            </p>
            <p className="text-sm text-[var(--slate)]">
              Move the map or adjust filters to try again.
            </p>
          </div>
        ) : listings.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center p-8 text-center">
            <div className="mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--sand)]">
              <Home size={28} className="text-[var(--slate-blue)]" />
            </div>
            <p className="font-semibold text-[var(--navy)]">No homes in view</p>
            <p className="mt-1 text-sm text-[var(--slate)]">
              Zoom out or pan the map, or loosen your filters.
            </p>
          </div>
        ) : (
          <div
            className={`grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 transition-opacity ${
              refetching ? "opacity-60" : "opacity-100"
            }`}
          >
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                selected={listing.id === selectedId}
                comingSoon={comingSoon}
                isFavorite={isFavorite?.(listing.listingId)}
                onToggleFavorite={onToggleFavorite}
                onInquire={onInquire}
                isComparing={isComparing?.(listing.id)}
                compareDisabled={compareFull}
                onToggleCompare={onToggleCompare}
                onSelect={onSelect}
                onHover={onHover}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
